import { Trans, useLingui } from "@lingui/react/macro";
import { plural } from "@lingui/core/macro";
import { useId } from "react";
import { useForm } from "react-hook-form";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import { CustomDialog } from "@medoma/components/Dialog/Dialog";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { InlineAlert } from "@medoma/components/InlineAlert/InlineAlert";
import { Text } from "@medoma/components/Text/Text";
import { parseISO } from "date-fns";
import { humanFormat } from "@medoma/models/date-and-time";
import { daysAgo } from "@/Utils/DateUtils";
import type { IExistingPatient } from "@medoma/models/patients";
import {
  formatPersonalIdentityNumber,
  getPatientFullName,
} from "@medoma/models/patients";
import Checkbox from "@/components/Checkbox/Checkbox";
import { DescriptionList } from "@/components/DescriptionList/DescriptionList";
import Form from "@/components/Form/Form";
import styles from "./Discharge.module.scss";

type IDischargeReviewFormData = {
  acknowledgedMissingInvoicingCodes: boolean;
};

export const DischargeReview = ({
  patient,
  dischargeDate,
  activitiesToCloseCount,
  activitiesToKeepCount,
  missingInvoicingCodesNames,
  disableSubmitButton,
  onCancel,
  onDischarge,
}: {
  patient: IExistingPatient;
  dischargeDate: string;
  activitiesToCloseCount: number;
  activitiesToKeepCount: number;
  missingInvoicingCodesNames: string[];
  disableSubmitButton: boolean;
  onCancel: () => void;
  onDischarge: () => void;
}) => {
  const { t } = useLingui();
  const formId = useId();
  const patientName = getPatientFullName(patient.name);
  const date = parseISO(dischargeDate);

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<IDischargeReviewFormData>({
    defaultValues: { acknowledgedMissingInvoicingCodes: false },
  });

  const validateAndSubmit = handleSubmit(() => {
    onDischarge();
  });

  return (
    <CustomDialog
      isOpen={true}
      onClose={onCancel}
      title={t`Granska utskrivning`}
      preferredWidth={720}
      actions={
        <>
          <OutlinedButton onClick={onCancel}>{t`Avbryt`}</OutlinedButton>
          <FilledButton
            disabled={disableSubmitButton}
            type="submit"
            form={formId}
          >
            {t`Skriv ut`}
          </FilledButton>
        </>
      }
    >
      <Form id={formId} onSubmit={validateAndSubmit}>
        <div className={styles.review}>
          <DescriptionList>
            <DescriptionList.Term>
              <Trans>Patient</Trans>
            </DescriptionList.Term>
            <DescriptionList.Description>
              {patientName}
            </DescriptionList.Description>
            <DescriptionList.Term>
              <Trans>Personnummer</Trans>
            </DescriptionList.Term>
            <DescriptionList.Description>
              {formatPersonalIdentityNumber({
                personalIdentityNumber: patient.personalIdentityNumber,
                personalIdentityNumberType: patient.personalIdentityNumberType,
              })}
            </DescriptionList.Description>
            <DescriptionList.Term>
              <Trans>Utskrivningsdatum</Trans>
            </DescriptionList.Term>
            <DescriptionList.Description>
              <Text element="span" weight="medium">
                {humanFormat(date, {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </Text>{" "}
              <Text element="span" color="faded">
                ({daysAgo(date)})
              </Text>
            </DescriptionList.Description>
            {activitiesToCloseCount > 0 || activitiesToKeepCount > 0 ? (
              <>
                <DescriptionList.Term>
                  <Trans>Aktiviteter</Trans>
                </DescriptionList.Term>
                <DescriptionList.Description>
                  <ul className={styles.consequences}>
                    {activitiesToCloseCount > 0 ? (
                      <li>
                        {plural(activitiesToCloseCount, {
                          one: "# aktivitet avslutas",
                          other: "# aktiviteter avslutas",
                        })}
                      </li>
                    ) : null}
                    {activitiesToKeepCount > 0 ? (
                      <li>
                        {plural(activitiesToKeepCount, {
                          one: "# aktivitet fortsätter",
                          other: "# aktiviteter fortsätter",
                        })}
                      </li>
                    ) : null}
                  </ul>
                </DescriptionList.Description>
              </>
            ) : null}
          </DescriptionList>
          <Text element="p">
            <Trans>
              <Text element="span" weight="bold">
                Utskrivningen går inte att ångra.
              </Text>{" "}
              Vårdperioden avslutas. Om patienten återregistreras skapas en ny
              vårdperiod.
            </Trans>
          </Text>
          {missingInvoicingCodesNames.length > 0 ? (
            <>
              <InlineAlert
                type="warning"
                title={t`Följande åtgärdskoder har inte rapporterats in för vårdperioden`}
                message={missingInvoicingCodesNames.join(", ")}
                placement="fill"
              />
              {/* The error goes below the checkbox, where it reads as feedback
                  on it rather than as a heading above it */}
              <div className={styles.errorAndConfirmation}>
                <Checkbox
                  label={{
                    text: t`Jag vill skriva ut patienten trots att åtgärdskoder saknas`,
                  }}
                  {...register("acknowledgedMissingInvoicingCodes", {
                    required: {
                      value: true,
                      message: t`Bekräfta att du vill skriva ut patienten trots att åtgärdskoder saknas`,
                    },
                  })}
                />
                {errors.acknowledgedMissingInvoicingCodes?.message ? (
                  <ErrorMessage
                    message={errors.acknowledgedMissingInvoicingCodes.message}
                    weight="bold"
                  />
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </Form>
    </CustomDialog>
  );
};
